<template>
  <div
    class="d-sticky"
    :class="{'with-margin' : !noMargin}"
  >
    <v-skeleton-loader
      v-if="loading"
      type="list-item-avatar-two-line"
      class="mb-2"
    />
    <v-banner
      v-if="!loading"
      single-line
      sticky
      color="party_header"
      class="mb-2"
    >
      <template v-if="create">
        <span>
          <strong>
            New Party
          </strong>
        </span>
      </template>

      <template v-else>
        <v-row
          align="center"
        >
          <v-col
            cols="auto"
            class="my-1"
          >
            <v-hover v-slot="{ hover }">
              <template v-if="peUserCanUpdate">
                <v-badge
                  bordered
                  color="secondary"
                  icon="mdi-pencil"
                  overlap
                  :offset-x="16"
                  :offset-y="16"
                >
                  <v-avatar
                    v-if="!hover"
                    size="56"
                    color="blue-grey darken-5"
                  >
                    <img
                      v-if="hasPhoto"
                      :src="peGetPhotoDataUrl"
                    >
                    <span
                      v-else
                      class="white--text text-caption font-weight-bold"
                    >No<br>Photo</span>
                  </v-avatar>
                  <v-avatar
                    v-else
                    size="56"
                    color="blue-grey darken-5"
                    class="pointer"
                  >
                    <v-btn
                      icon
                      @click="showPhotoUploadDialog = true"
                    >
                      <v-icon color="white">
                        mdi-camera-plus
                      </v-icon>
                    </v-btn>
                  </v-avatar>
                </v-badge>
              </template>
              <template v-else>
                <v-avatar
                  size="56"
                  color="blue-grey darken-5"
                >
                  <img
                    v-if="hasPhoto"
                    :src="peGetPhotoDataUrl"
                  >
                  <span
                    v-else
                    class="white--text text-caption font-weight-bold"
                  >No<br>Photo</span>
                </v-avatar>
              </template>
            </v-hover>
          </v-col>
          <v-col>
            <span>
              <strong class="text-uppercase">
                {{ partyFullName }}
              </strong>

              <div
                class="text-caption d-block"
              >
                {{ peParty.gender_string }} | DOB:
                {{ peParty.dob ? dateDisplayFormat : '-' }}
                <strong
                  v-if="peParty.dob || peParty.uncertain_dob"
                >
                  ({{ calculatedAge }})
                </strong>
                {{
                  aNumberFormatDisplay
                }}
              </div>
            </span>
            <span>
              <v-chip
                v-for="tag of peParty.tags"
                :key="tag.id"
                class="ma-2"
                close
                small
                :color="tag.color"
                text-color="white"
                @click:close="onUnTagParty(tag)"
              >
                {{ tag.name }}
              </v-chip>
            </span>
          </v-col>
          <DocketwiseButtons />
          <EcoMemberPass />
          <v-col cols="auto">
            <v-tooltip bottom>
              <template
                #activator="{on, attrs}"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="onCollapseToggle"
                >
                  <v-icon>
                    {{ collapsableIcon }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ collapsableText }}</span>
            </v-tooltip>

            <v-tooltip
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="showRecords"
                >
                  <v-icon>
                    plagiarism
                  </v-icon>
                </v-btn>
              </template>

              <span>Associated Records</span>
            </v-tooltip>

            <v-tooltip
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  :color="adverseIndividualsIconColor"
                  v-on="on"
                  @click="showAdverseIndividuals"
                >
                  <v-icon>
                    warning_amber
                  </v-icon>
                </v-btn>
              </template>
              <span>View/Add Adverse Individuals</span>
            </v-tooltip>

            <v-tooltip
              v-if="peUserCanUpdate"
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="onShowTagParty"
                >
                  <v-icon>
                    mdi-tag-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Tag Parties</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
    </v-banner>
    <!-- Records dialog -->
    <RecordsDialog
      :display-dialog="showRecordsDialog"
      :party-name="partyFullName"
      :records="recordsList"
      @close-dialog="showRecordsDialog = false"
    />
    <!-- Adverse Individuals Dialog -->
    <AdverseIndividualDialog
      :adverse-individual-data="adverseIndividualsList"
      :adverse-to="peParty.id"
      :view-only="false"
    />
    <PEPartyPhotoDialog
      v-model="showPhotoUploadDialog"
    />
    <TagPartyDialog />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { aNumberFormatDisplay, calculatedAge, dateDisplayFormat } from '@/utilities'
import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import AdverseIndividualDialog from '@/components/Dialogs/AdverseIndividual.vue'
import DocketwiseButtons from '@/components/Party/PartyEdit/HeaderTools/DocketwiseButtons.vue'
import EcoMemberPass from '@/components/Party/PartyEdit/HeaderTools/EcoMemberPass.vue'
import PEPartyPhotoDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyPhotoDialog.vue'
import RecordsDialog from '@/components/Dialogs/Records.vue'
import TagPartyDialog from '@/components/Dialogs/Party/TagParty'

export default {
  name: 'PEHeader',

  components: {
    AdverseIndividualDialog,
    DocketwiseButtons,
    EcoMemberPass,
    PEPartyPhotoDialog,
    RecordsDialog,
    TagPartyDialog,
  },

  props: {
    create: {
      type: Boolean,
      default: false,
    },

    noMargin: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      collapsed: false,
      showPhotoUploadDialog: false,
      showRecordsDialog: false,
    }
  },

  computed: {
    aNumberFormatDisplay () {
      return this.peParty.a_number ? `| A# ${aNumberFormatDisplay(this.peParty.a_number)}` : ''
    },

    adverseIndividualsIconColor () { return this.adverseIndividualsList.length > 0 ? 'red' : '' },
    adverseIndividualsList () { return this.peParty?.adverse_individuals || [] },
    calculatedAge () { return calculatedAge(this.peParty.dob, this.peParty.uncertain_dob) },
    collapsableIcon () { return this.collapsed ? 'unfold_more' : 'unfold_less' },
    collapsableText () { return this.collapsed ? 'Expand All' : 'Collapse All' },
    dateDisplayFormat () { return dateDisplayFormat(this.peParty.dob) },

    hasPhoto () {
      return !!this.peGetPhotoDataUrl
    },

    loading () {
      if (this.create) return false
      return this.pePartyLoading
    },

    partyFullName () {
      const headerString = [this.peParty.first_name]
      if (this.peParty.middle_name) headerString.push(this.peParty.middle_name)
      headerString.push(this.peParty.last_name)
      headerString.push(`(ID: ${this.peParty.id})`)

      return headerString.join(' ')
    },

    recordsList () { return this.peParty?.records || [] },

    ...mapGetters(PARTY_NAMESPACE, [
      'peGetPhotoDataUrl',
      'peParty',
      'pePartyLoading',
      'peUserCanUpdate',
    ]),
  },

  methods: {
    onCollapseToggle () {
      this.collapsed = !this.collapsed
      this.peSetCollapsedAllCards(this.collapsed)
    },

    onShowTagParty () {
      this.setShowTagPartyDialog(true)
    },

    onUnTagParty (tag) {
      this.untagParty({ id: this.peParty.id, tagId: tag.id })
    },

    showAdverseIndividuals () { this.setViewAdverseIndividualsDialog(true) },
    showRecords () { this.showRecordsDialog = true },

    ...mapActions(PARTY_NAMESPACE, [
      'peSetCollapsedAllCards',
      'untagParty',
    ]),

    ...mapMutations(PARTY_NAMESPACE, [
      'setShowTagPartyDialog',
    ]),

    ...mapMutations([
      'setViewAdverseIndividualsDialog',
    ]),
  },
}
</script>

<style scoped lang="scss">
.d-sticky {
  position: sticky !important;
  z-index: 1;
  top: 0px;
  &.with-margin {
    top: 72px;
  }
}
</style>
